
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import httpCodes from "http-status-codes";
import { JwtAuthService, setTokenRefresh, shouldRefreshToken } from "./authentication";
import { UserCache } from "./caches";
import { handleDates, reviveDateTime } from "../common";
import { Buffer } from "buffer";
import { ForbiddenError, NotFoundError, NTError, UnauthorizedError } from "@nt-portal/server-sdk";
import { FileData, FormKeys, IApiFileService } from "@nt-portal/common-sdk";
import { FileUploadError } from "../types";

// @ts-ignore
window.Buffer = Buffer;

type ApiRequestOptions = {
    withCredentials?: boolean;
    /** Keep JSON strings intact for document payloads whose text may resemble dates. */
    reviveDates?: boolean;
};

export class ApiService implements IApiFileService {

    private _headers: any;

    private toBuffer(ab: ArrayBuffer): Buffer {
        var buf = Buffer.alloc(ab.byteLength);
        var view = new Uint8Array(ab);
        for (var i = 0; i < buf.length; ++i) {
            buf[i] = view[i];
        }
        return buf;
    }

    public static cleanUrl(url: string): string {
        let pos = url.indexOf("://");
        let prefix = '';
        let rest = ''
        if (pos !== -1) {
            prefix = url.substring(0, pos + 3);
            rest = url.substring(pos + 3);
        } else {
            rest = url;
        }
        while (rest.indexOf("//") !== -1) {
            rest = rest.replace("//", "/");
        }
        return prefix + rest;
    }

    public async put(url: string, token: string, data: any, options?: ApiRequestOptions): Promise<any> {
        return await this.makeCall(url, "put", token, data, options);
    }
    public async post(url: string, token: string, data: any, options?: ApiRequestOptions): Promise<any> {
        return await this.makeCall(url, "post", token, data, options);
    }
    public async get(url: string, token: string, options?: ApiRequestOptions): Promise<any> {
        return await this.makeCall(url, "get", token, null, options);
    }
    public async delete(url: string, token: string, options?: ApiRequestOptions): Promise<any> {
        return await this.makeCall(url, "delete", token, null, options);
    }

    public static deserialize(data: string): any {
        return JSON.parse(data, reviveDateTime);
    }

    private async getToken() {
        let token = "";
        const currentToken = UserCache.idToken;

        let isValid = JwtAuthService.isAuthenticated();
        if (!isValid) {
            return "";
        }

        if (shouldRefreshToken()) {
            let service = new JwtAuthService();
            token = await service.silentLogin();
            setTokenRefresh();
        } else {
            const currentToken = UserCache.idToken;
            token = currentToken;
        }

        return token;
    }

    public setHeaders(value: any) {
        this._headers = value;
    }


    public onProgress?: (value: number) => void;

    private async makeCall(url: string, method: string, token: string, data: any, options?: ApiRequestOptions): Promise<any> {

        // let body = (data) ? JSON.stringify(data) : null;

        url = ApiService.cleanUrl(url);

        url = encodeURI(url);


        const requestOptions: AxiosRequestConfig = {
            method: method,
            url: url,
            withCredentials: options?.withCredentials ?? true
        };

        if (!token) {
            token = await this.getToken();
        }

        if (token && token !== "") {
            requestOptions.headers = { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
        } else {
            requestOptions.headers = { "Content-Type": "application/json" };
        }

        // console.log(`${method}: URL: ${url} - Json: ${requestOptions.body}`)

        let error: Error | null = null;

        const axiosClient = axios.create(requestOptions);
        axiosClient.interceptors.response.use(originalResponse => {
            if (options?.reviveDates !== false) {
                handleDates(originalResponse.data);
            }
            return originalResponse;
        });

        try {
            let response = await axiosClient.request({ ...requestOptions, data: data ? data : undefined });
            if (response !== undefined) {
                return response.data;
            }
        } catch (ex) {
            let axiosError = ex as AxiosError;
            let response = axiosError.response;
            if (response) {
                let text = response.data as string;
                switch (response.status) {
                    case httpCodes.NOT_FOUND: error = new NotFoundError(text); break;
                    case httpCodes.INTERNAL_SERVER_ERROR: error = new NTError(text); break;
                    case httpCodes.CONFLICT: error = new NTError(text); break;
                    case httpCodes.UNAUTHORIZED: error = new UnauthorizedError(text); break;
                    case httpCodes.FORBIDDEN: error = new ForbiddenError(text); break;
                    case httpCodes.BAD_REQUEST: error = new NTError(text); break;
                }
            } else {
                console.log(`Result: URL: ${url} - Error: ${axiosError}`);
                throw new NTError(axiosError.message);
            }

            if (error)
                throw error;
            return "";

        }


    }

    public async getFile(url: string, token: string): Promise<ArrayBuffer> {
        return await this.callFile(url, 'get', token);

    }

    public async postFile(url: string, token: string, data?: any): Promise<ArrayBuffer> {
        return await this.callFile(url, 'post', token, data);
    }

    private async callFile(url: string, method: string, token: string, data?: any): Promise<ArrayBuffer> {

        let body = (data) ? JSON.stringify(data) : null;

        url = ApiService.cleanUrl(url);

        const requestOptions: RequestInit = {
            method: method,
            body: body
        };

        if (!token) {
            token = await this.getToken();
        }

        if (token && token !== "") {
            requestOptions.headers = { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
        } else {
            requestOptions.headers = { "Content-Type": "application/json" };
        }


        let response = await fetch(url, requestOptions)
        if (!response)
            throw new NTError("No returned response.")

        if (response.status === 200 && response.body) {
            const reader = response.body.getReader();

            const contentLengthValue = response.headers.get('Content-Length') ?? '0'
            const contentLength = parseInt(contentLengthValue);
            let receivedLength = 0
            let chunks = [];
            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                chunks.push(value);
                receivedLength += value.length;

                if (this.onProgress) {
                    let percentage = (receivedLength / contentLength) * 100;
                    this.onProgress(percentage)
                }
            }

            let chunksAll = new Uint8Array(receivedLength); // (4.1)
            let position = 0;
            for (let chunk of chunks) {
                chunksAll.set(chunk, position); // (4.2)
                position += chunk.length;
            }

            // Return a true ArrayBuffer. Use a slice to account for any non-zero byteOffset.
            const arrayBuffer = chunksAll.buffer.slice(chunksAll.byteOffset, chunksAll.byteOffset + chunksAll.byteLength);
            return arrayBuffer
        } else {
            const f = response
            const text = await f.text();
            switch (f.status) {
                case httpCodes.NOT_FOUND: throw new NotFoundError(text)
                case httpCodes.INTERNAL_SERVER_ERROR: throw new NTError(text);
                case httpCodes.CONFLICT: throw new NTError(text);
                case httpCodes.UNAUTHORIZED: throw new UnauthorizedError(text);
                case httpCodes.FORBIDDEN: throw new ForbiddenError(text);
                case httpCodes.BAD_REQUEST: throw new NTError(text);

            }

        }

        // Return an empty ArrayBuffer on non-200 without throwing (caller may handle upstream)
        return new ArrayBuffer(0);

    }

    //TODO: needs to be updated to Axios when we need it.
    public async uploadFile(url: string, token: string, fileKey: string, file: File, method?: string, additionalData?: FormKeys[]): Promise<any> {
        // NOTE: Progress support added using Axios onUploadProgress. The class-level onProgress callback
        // will receive a numeric percentage (0-100). This keeps the existing signature (required by IApiFileService)
        // while enhancing functionality.
        url = ApiService.cleanUrl(url);

        // Verify the file is readable before attempting upload. A locked file (held
        // open exclusively by another process) will throw a NotReadableError here
        // rather than producing a cryptic network/axios failure mid-upload.
        try {
            await file.slice(0, 1).arrayBuffer();
        } catch (readError) {
            if (readError instanceof DOMException && readError.name === 'NotReadableError') {
                throw new NTError(`The file "${file.name}" is locked or in use by another application. Close it and try again.`);
            }
            throw readError;
        }

        const formData = new FormData();
        formData.append(fileKey, file, file.name);
        if (additionalData) {
            additionalData.forEach(k => formData.append(k.key, k.value));
        }

        if (!token) {
            token = await this.getToken();
        }

        // Build Axios config. Don't set Content-Type explicitly; Axios will set the multipart boundary.
        const config: AxiosRequestConfig = {
            method: method || "post",
            url: encodeURI(url),
            data: formData,
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            onUploadProgress: (progressEvent) => {
                if (!this.onProgress) return;
                const { loaded, total } = progressEvent;
                if (total) {
                    const pct = (loaded / total) * 100;
                    this.onProgress(pct);
                } else {
                    // Fallback: emit -1 to indicate indeterminate progress if total not provided
                    this.onProgress(-1);
                }
            }
        };



        try {
            const response = await axios.request(config);
            // Axios auto-parses JSON; ensure date handling consistency
            handleDates(response.data);
            return response.data;
        } catch (ex) {
            const axiosError = ex as AxiosError;
            const response = axiosError.response;
            if (response) {
                const dataText = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
                switch (response.status) {
                    case httpCodes.NOT_FOUND: throw new NotFoundError(dataText);
                    case httpCodes.INTERNAL_SERVER_ERROR: throw new NTError(dataText);
                    case httpCodes.CONFLICT: throw new NTError(dataText);
                    case httpCodes.UNAUTHORIZED: throw new UnauthorizedError(dataText);
                    case httpCodes.FORBIDDEN: throw new ForbiddenError(dataText);
                    case httpCodes.BAD_REQUEST: throw new NTError(dataText);
                    default: throw new NTError(dataText || axiosError.message);
                }
            } else {
                if (axiosError.code === "ERR_NETWORK") {
                    throw new FileUploadError(`Network error while uploading "${file.name}".`);
                }
            }

            console.log(`Result: URL: ${url} - Error: ${axiosError}`);
            throw new NTError(axiosError.message);
        }
    }


    public async getFileAdv(url: string, token: string, data?: any, method?: string): Promise<FileData> {


        let body = (data) ? JSON.stringify(data) : null;

        url = ApiService.cleanUrl(url);

        const requestOptions: RequestInit = {
            method: method?.toLowerCase() ?? "get",
            body: body
        };

        if (!token) {
            token = await this.getToken();
        }

        if (token && token !== "") {
            requestOptions.headers = { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
        } else {
            requestOptions.headers = { "Content-Type": "application/json" };
        }


        let response = await fetch(url, requestOptions)
        if (!response)
            throw new NTError("No returned response.")

        if (response.status === 200 && response.body) {
            const reader = response.body.getReader();

            const disposition = response.headers.get('Content-Disposition') || '';
            let fileName = '';
            // Parse filename*= or filename=
            const match = disposition.match(/filename\\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i);
            if (match) {
                fileName = decodeURIComponent(match[1] || match[2]);
            }

            const mimeType = response.headers.get('Content-Type') || '';

            const contentLengthValue = response.headers.get('Content-Length') ?? '0'
            const contentLength = parseInt(contentLengthValue);
            let receivedLength = 0
            let chunks = [];
            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    break;
                }

                chunks.push(value);
                receivedLength += value.length;

                if (this.onProgress) {
                    let percentage = (receivedLength / contentLength) * 100;
                    this.onProgress(percentage)
                }
            }

            let chunksAll = new Uint8Array(receivedLength); // (4.1)
            let position = 0;
            for (let chunk of chunks) {
                chunksAll.set(chunk, position); // (4.2)
                position += chunk.length;
            }

            // Return a true ArrayBuffer. Use a slice to account for any non-zero byteOffset.
            const arrayBuffer = chunksAll.buffer.slice(chunksAll.byteOffset, chunksAll.byteOffset + chunksAll.byteLength);
            return {
                buffer: arrayBuffer,
                filename: fileName,
                mimeType: mimeType
            }
        } else {
            const f = response
            const text = await f.text();
            switch (f.status) {
                case httpCodes.NOT_FOUND: throw new NotFoundError(text)
                case httpCodes.INTERNAL_SERVER_ERROR: throw new NTError(text);
                case httpCodes.CONFLICT: throw new NTError(text);
                case httpCodes.UNAUTHORIZED: throw new UnauthorizedError(text);
                case httpCodes.FORBIDDEN: throw new ForbiddenError(text);
                case httpCodes.BAD_REQUEST: throw new NTError(text);

            }

        }

        // Return an empty ArrayBuffer on non-200 without throwing (caller may handle upstream)
        return {
            buffer: new ArrayBuffer(0),
            filename: "",
            mimeType: ""
        }

    }

}
